/**
 * Pure date utilities for consistent date handling across frontend and backend.
 * All functions operate in the local timezone to prevent off-by-one day errors.
 */

/**
 * Parse a YYYY-MM-DD string without timezone conversion.
 * Use this for date-only fields to prevent UTC-shift off-by-one errors.
 */
export function parseDateOnly(dateString: string): Date {
  if (!dateString) return new Date();
  const parts = dateString.split("-").map(Number);
  return new Date(parts[0] ?? 0, (parts[1] ?? 1) - 1, parts[2] ?? 1);
}

/**
 * A bare `YYYY-MM-DD` (a Postgres `@db.Date`), as opposed to a full timestamp.
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse for DISPLAY, without the timezone shift that silently loses a day.
 *
 * `new Date("2026-07-27")` is parsed as UTC midnight, which in every zone BEHIND
 * UTC renders as Jul 26 — an off-by-one on move-in dates, due dates, DOBs and
 * audit grant windows. A date-only string names a calendar day, so it is built
 * as a LOCAL day.
 *
 * This is the POLYMORPHIC form: date-only strings become local days, full
 * timestamps pass through, anything unparseable becomes null. It is deliberately
 * NOT the same function as `parseDateOnly`, which splits on "-" and therefore
 * yields an Invalid Date for a timestamp. They are not interchangeable — see
 * US-151 Constraint 2.
 */
export function parseDisplayDate(
  value: string | Date | null | undefined,
): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (DATE_ONLY.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y as number, (m as number) - 1, d as number);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Local midnight, so day arithmetic is calendar-based rather than elapsed-ms. */
function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * date-fns-style relative time: "just now", "5 minutes", "3 hours", "7 days",
 * with `addSuffix` turning those into "in 7 days" / "3 hours ago".
 *
 * THE DAY TIER COUNTS CALENDAR DAYS, which is the F-077 fix. Truncating elapsed
 * milliseconds made an invitation minted with a 7-day window read "in 6 days"
 * the moment it was created, because 6.99 elapsed days floors to 6. Counting
 * between local midnights also survives DST, where a day is 23 or 25 hours.
 *
 * The sub-day tiers deliberately stay elapsed-time based — "in 3 hours" is what
 * an operator wants, and rounding those to a calendar day would be worse.
 */
export function formatRelativeTime(
  target: string | Date,
  options?: { addSuffix?: boolean },
  now: Date = new Date(),
): string {
  const t = parseDisplayDate(target);
  if (!t) return "";
  const seconds = Math.floor((t.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);

  let s: string;
  if (abs < 60) return "just now";
  if (abs < 3600) {
    const m = Math.floor(abs / 60);
    s = `${m} minute${m !== 1 ? "s" : ""}`;
  } else if (abs < 86400) {
    const h = Math.floor(abs / 3600);
    s = `${h} hour${h !== 1 ? "s" : ""}`;
  } else {
    const d = Math.abs(
      Math.round(
        (startOfLocalDay(t).getTime() - startOfLocalDay(now).getTime()) /
          86400000,
      ),
    );
    s = `${d} day${d !== 1 ? "s" : ""}`;
  }

  if (options?.addSuffix) return seconds < 0 ? `${s} ago` : `in ${s}`;
  return s;
}

/**
 * Get today's date in YYYY-MM-DD format (local timezone).
 */
export function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Convert a Date object to YYYY-MM-DD format (local timezone).
 */
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Check if two dates fall on the same calendar day (ignoring time). */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/** Check if a date is today. */
export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

/**
 * Return the start of the current frequency window, as a UTC-midnight Date.
 *
 * UTC basis is deliberate: submission dates are stored as `@db.Date` (Prisma
 * returns them at UTC midnight), so the window boundary must be UTC too or
 * comparisons drift a day for any runtime/timezone west of UTC.
 *
 * Returns null for "on_demand" (no window — always submittable) and for any
 * unrecognised frequency (callers must treat null conservatively, NOT as
 * "always satisfied").
 */
export function getFrequencyWindowStart(
  frequency: string,
  now: Date = new Date(),
): Date | null {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const d = now.getUTCDate();
  switch (frequency) {
    // No per-shift boundary is available in this pure helper, so a per_shift
    // requirement is windowed to the current day — filed today counts as filed
    // this period. (Closes the "never overdue" hole; refine if shift-aware
    // overdue detection is ever needed.)
    case "per_shift":
    case "daily":
      return new Date(Date.UTC(y, m, d));
    case "weekly": {
      const day = now.getUTCDay();
      const daysToMonday = day === 0 ? 6 : day - 1;
      return new Date(Date.UTC(y, m, d - daysToMonday));
    }
    case "monthly":
      return new Date(Date.UTC(y, m, 1));
    case "quarterly": {
      const quarter = Math.floor(m / 3);
      return new Date(Date.UTC(y, quarter * 3, 1));
    }
    case "semiannually":
      return new Date(Date.UTC(y, m < 6 ? 0 : 6, 1));
    case "annually":
      return new Date(Date.UTC(y, 0, 1));
    case "on_demand":
    default:
      return null;
  }
}

/**
 * Whether a resident form requirement is "filed" for its CURRENT period — the
 * single source of truth shared by the backend (requirement list, appointment
 * gap detection) and the frontend (row + detail badges) so they never diverge.
 *
 * - `one_shot`: terminal — `fulfilledAt` is the only signal (recurring
 *   frequencies never stamp it).
 * - `on_demand`: no window — any submission satisfies it.
 * - `lookback` (US-113 Need C): a rolling validity window — filed iff the latest
 *   submission is within `lookbackDays` days of `now`. A missing/non-positive
 *   `lookbackDays` is a misconfiguration → conservatively NOT filed.
 * - recurring (daily…annually): a submission inside the current frequency
 *   window (`getFrequencyWindowStart`).
 * - unrecognised frequency: conservatively NOT filed, so it surfaces as
 *   overdue/a gap rather than being silently hidden.
 *
 * Dates are compared on a UTC basis — pass `@db.Date` values directly, or
 * `new Date("YYYY-MM-DD")` for date-only strings (both are UTC midnight).
 */
export function isRequirementFiledForCurrentPeriod(opts: {
  frequency: string;
  fulfilledAt: Date | null;
  latestSubmissionDate: Date | null;
  /** US-113 Need C — the rolling validity window length; only read when
   *  `frequency === "lookback"`. */
  lookbackDays?: number | null;
  /** Override "now" for testability; defaults to the current instant. */
  now?: Date;
}): boolean {
  const now = opts.now ?? new Date();
  if (opts.frequency === "one_shot") return opts.fulfilledAt !== null;
  if (opts.frequency === "on_demand") return opts.latestSubmissionDate !== null;
  if (opts.frequency === "lookback") {
    if (opts.lookbackDays == null || opts.lookbackDays <= 0) return false;
    if (opts.latestSubmissionDate === null) return false;
    const cutoff = new Date(
      now.getTime() - opts.lookbackDays * 24 * 60 * 60 * 1000,
    );
    return opts.latestSubmissionDate >= cutoff;
  }
  const windowStart = getFrequencyWindowStart(opts.frequency, now);
  if (windowStart === null) return false; // unrecognised → conservatively unfiled
  return (
    opts.latestSubmissionDate !== null &&
    opts.latestSubmissionDate >= windowStart
  );
}
